/* ────────────────────────────────────────────────────────────────────
 * Legal content (Arabic / العربية) — Modern Standard Arabic.
 *
 * Style rules for ALL Arabic copy: docs/arabic-style-guide.md (locked
 * 2026-07-12 — MSA fluency + بلاغة; the notes below are its highlights).
 *
 * Translated from web/src/copy/legal-en.ts after owner approval of the
 * English Privacy Policy (2026-07-12), then given an idiomatic pass the
 * same day (owner: "feels like literal translation") — meaning is kept
 * 1:1 with the approved English; syntax is restructured into natural
 * Arabic legal register. Rules followed:
 *   · Modern Standard Arabic (MSA), legal register but plainly worded.
 *   · No harakat/diacritics unless a word would otherwise be ambiguous.
 *   · No em dashes (—) or en dashes (–), matching the English house rule.
 *   · Brand names transliterated as elsewhere in the app: برَينوتس / برييورا.
 *   · Product/vendor names that are Latin trademarks stay Latin (OpenAI,
 *     Google Gemini, Cloudflare, Sentry, Moyasar, Google Workspace, etc.).
 *   · Sub-processor terminology: مزود الخدمات / مزودو / مزودي (footer-aligned).
 *   · No pronoun-echo padding (حشو التوكيد): «جهازك أنت»، «نخزن نحن»، «الموقعين
 *     كليهما»، «يقتصر ... دون سواها» are all trimmed (owner rule 2026-07-12:
 *     بلاغة with simplicity — emphasis only where it carries real contrast).
 *   · Registration identifier: الرقم الوطني الموحد (per the MoC certificate).
 *
 * All three documents are translated (Privacy 2026-07-12; Terms and
 * Sub-processors 2026-07-13, following docs/arabic-style-guide.md and the
 * terminology locked during the Privacy review). The renderer + types are
 * shared with the English file; markup is the same (**bold**, [label](href)).
 * LegalPage falls back to the English document for any key not present here.
 * ──────────────────────────────────────────────────────────────────── */

import type { LegalDoc } from './legal-en';

/* The National Number is deliberately NOT repeated here: it lives on the About
 * page only (owner rule 2026-07-14). Guarded by legal-pages.spec.ts. */
const IDENTITY_AR =
  '**شركة برييورا** (Priyora Company) · جدة 23532، المملكة العربية السعودية.';

const PRIVACY_AR: LegalDoc = {
  key: 'privacy',
  eyebrow: 'قانوني',
  title: 'سياسة الخصوصية',
  updated: 'آخر تحديث 28 يوليو 2026',
  intro:
    'تُبين سياسة الخصوصية هذه كيف نجمع في شركة برييورا بياناتك الشخصية، وكيف نستخدمها ونخزنها ونشاركها ونحميها، عند استخدامك برَينوتس، خدمتنا لتدوين محاضر الاجتماعات، ويشمل ذلك موقع برَينوتس (prinotes.com) وتطبيقه (app.prinotes.com). نلتزم بحماية خصوصيتك وفقا لنظام حماية البيانات الشخصية السعودي ولائحته التنفيذية، وننطلق من مبدأ واحد بسيط: بياناتك ملك لك.',
  sections: [
    {
      id: 'introduction',
      n: '01',
      title: 'المقدمة والنطاق',
      blocks: [
        {
          t: 'p',
          c: 'برَينوتس خدمة تقدمها **شركة برييورا**، وهي شركة مسجلة في المملكة العربية السعودية. وحيثما ورد في هذه السياسة اسم "برَينوتس" فالمقصود به المنتج، أما "برييورا" أو "نحن" وما في حكمها فالمقصود بها شركة برييورا بوصفها الجهة المسؤولة عن بياناتك الشخصية (**جهة التحكم في البيانات**).',
        },
        {
          t: 'p',
          c: 'تسري هذه السياسة على كل من يزور موقعنا أو يستخدم تطبيقنا (ويُشار إليهما معا بـ"الخدمة")، سواء أكنت مستخدما بصفتك الفردية أم عضوا في منشأة تستخدم برَينوتس.',
        },
        {
          t: 'p',
          c: 'وباستخدامك الخدمة، تقر بأنك قرأت هذه السياسة وفهمتها. ومتى اشترط النظام موافقتك على معالجة بعينها، طلبناها منك صراحة وعلى نحو مستقل، ولن يُطلب منك أبدا أن توافق على استخدامات لا صلة بينها في خطوة واحدة.',
        },
        {
          t: 'p',
          c: 'وقد صممنا برَينوتس ليجمع **الحد الأدنى من البيانات اللازمة** لتقديم الخدمة، وألا يحتفظ بها إلا حين تعود عليك بفائدة مباشرة.',
        },
      ],
    },
    {
      id: 'who-we-are',
      n: '02',
      title: 'من نحن (جهة التحكم في البيانات)',
      blocks: [
        { t: 'p', c: 'جهة التحكم في البيانات المسؤولة عن بياناتك الشخصية هي:' },
        {
          t: 'ul',
          c: [
            '**شركة برييورا** (Priyora Company)، شركة ذات مسؤولية محدودة بشخص واحد، مسجلة في المملكة العربية السعودية.',
            '**مقر التسجيل:** جدة 23532، المملكة العربية السعودية.',
            '**مسجلة لدى:** السجل الوطني لحماية البيانات الشخصية (NDGP، سدايا)، برقم تسجيل **3260007171**.',
          ],
        },
        {
          t: 'p',
          c: 'لأي استفسار عن هذه السياسة، أو لممارسة حقوقك، راسل فريق الخصوصية لدينا عبر [privacy@prinotes.com](mailto:privacy@prinotes.com)',
        },
        {
          t: 'p',
          c: 'وللمساعدة العامة، راسلنا عبر [support@prinotes.com](mailto:support@prinotes.com)',
        },
        {
          t: 'p',
          c: 'ومتى أوجب النظام ذلك، سنعين مسؤولا لحماية البيانات.',
        },
      ],
    },
    {
      id: 'definitions',
      n: '03',
      title: 'التعريفات',
      blocks: [
        {
          t: 'ul',
          c: [
            '**البيانات الشخصية**: كل معلومة تدل على هويتك أو يمكن ربطها بك بصورة معقولة، كاسمك أو بريدك الإلكتروني أو صوتك.',
            '**محتوى الاجتماع**: التسجيلات الصوتية التي تسجلها أو ترفعها، والنصوص التي نفرغها منها، وما نستخلصه منها من ملخصات ومحاضر وقرارات ومهام.',
            '**المعالجة**: أي عملية تُجرى على البيانات الشخصية، كجمعها أو تخزينها أو استخدامها أو مشاركتها أو حذفها.',
            '**مزود الخدمات**: طرف ثالث يعالج البيانات الشخصية نيابة عنا ليعيننا على تقديم الخدمة.',
            '**الخدمة**: موقع برَينوتس وتطبيقه، وجميع الميزات المرتبطة بهما.',
            '**نظام حماية البيانات الشخصية (PDPL)**: النظام المعمول به في المملكة لحماية البيانات الشخصية، وتتولى تنظيمه وإنفاذه [الهيئة السعودية للبيانات والذكاء الاصطناعي (سدايا)](https://sdaia.gov.sa).',
            '[**منصة حوكمة البيانات الوطنية (NDGP)**](https://dgp.sdaia.gov.sa): منصة وطنية تشغلها [الهيئة السعودية للبيانات والذكاء الاصطناعي (سدايا)](https://sdaia.gov.sa)، وتستضيف السجل الوطني لحماية البيانات الشخصية.',
          ],
        },
      ],
    },
    {
      id: 'what-we-collect',
      n: '04',
      title: 'المعلومات التي نجمعها',
      blocks: [
        { t: 'h', c: 'بيانات الحساب والهوية' },
        {
          t: 'p',
          c: 'تسجل دخولك عبر **حساب Google أو Microsoft**، فبرَينوتس لا يستخدم كلمات مرور أصلا. وعند تسجيل الدخول نتلقى من مزود حسابك اسمك وبريدك الإلكتروني وصورة ملفك الشخصي ومعرف الحساب.',
        },
        { t: 'h', c: 'محتوى الاجتماع' },
        {
          t: 'p',
          c: 'عندما تسجل اجتماعا أو ترفعه، نعالج **الصوت**، و**النص المفرغ** الذي ننشئه منه، وما نستخلصه من **ملخصات ومحاضر وقرارات ومهام**، إضافة إلى تفاصيل الاجتماع التي تدخلها بنفسك، كالعناوين والتواريخ وأسماء المشاركين.',
        },
        {
          t: 'p',
          c: '**نحذف الصوت من أنظمتنا فور انتهاء المعالجة** بشكل افتراضي، ولا نبقي إلا النص المفرغ وما يُنشأ منه. ولا يُحفظ الصوت إلا إذا فعّلت الاحتفاظ بالصوت (انظر القسم 12). وإذا ربطت Drive أو OneDrive، فتُحفظ نسخة من الصوت في تخزينك السحابي ولا يطالها هذا الحذف (انظر القسم 10).',
        },
        { t: 'h', c: 'بيانات التكامل (عند الربط فقط)' },
        {
          t: 'p',
          c: 'إذا ربطت حسابك في **Google Workspace** أو **Microsoft 365**، فلا نصل إلا إلى ما تتطلبه الميزات التي تفعلها، كمواعيد التقويم والمهام والملفات المُنشأة عبر برَينوتس وجهات الاتصال التي نقرؤها لاقتراح المشاركين. وإذا منحت صلاحية Drive أو OneDrive، **فتُحفظ أصوات اجتماعاتك ونصوصها المفرغة في تخزينك السحابي تلقائيا ما لم توقف هذا الحفظ** (انظر القسم 10). ويمكنك إلغاء الربط متى شئت.',
        },
        { t: 'h', c: 'بيانات الفوترة' },
        {
          t: 'p',
          c: 'يقدم برَينوتس باقة مجانية وباقات مدفوعة. وإذا اشتركت في باقة مدفوعة، تتولى **Moyasar**، جهة الدفع لدينا في المملكة، معالجة المدفوعات. و**لا يتلقى برَينوتس رقم بطاقتك الكامل ولا يخزنه**، بل لا نخزن سوى تفاصيل محدودة كنوع البطاقة وأرقامها الأربعة الأخيرة وسجل اشتراكك وفواتيرك.',
        },
        { t: 'h', c: 'البيانات التقنية وبيانات الاستخدام' },
        {
          t: 'p',
          c: 'لتشغيل الخدمة وتأمينها، نعالج معلومات تقنية كنوع جهازك ومتصفحك، وعنوان بروتوكول الإنترنت (IP)، وسجلات النظام، وملفات الارتباط الأساسية المبينة في القسم 14.',
        },
        {
          t: 'p',
          c: '**ما اللازم وما الاختياري.** بيانات تسجيل الدخول، ومحتوى اجتماعاتك عند استخدامك الميزات الأساسية، لازمة لتقديم الخدمة، إذ لا يمكننا من دونها إنشاء حسابك ولا معالجة اجتماعاتك. أما ربط Google Workspace أو Microsoft 365، وتلقي الرسائل التسويقية، فاختياريان تماما، ولا يترتب على رفضهما سوى عدم توفر تلك الميزات أو الرسائل.',
        },
      ],
    },
    {
      id: 'how-we-use',
      n: '05',
      title: 'كيف نستخدم معلوماتك والأساس النظامي لذلك',
      blocks: [
        {
          t: 'p',
          c: 'نستخدم بياناتك الشخصية للأغراض الآتية، ويستند كل غرض منها إلى أساس نظامي محدد بموجب نظام حماية البيانات الشخصية:',
        },
        {
          t: 'table',
          head: ['الغرض', 'الأساس النظامي بموجب نظام حماية البيانات الشخصية'],
          rows: [
            [
              'تقديم الخدمة: تسجيل اجتماعاتك وتفريغها وتلخيصها ومزامنتها',
              'تنفيذ الاتفاقية التي تكون طرفا فيها (المادة 6(2))',
            ],
            [
              'التحقق من هويتك والحفاظ على أمان حسابك',
              'تنفيذ الاتفاقية التي تكون طرفا فيها (المادة 6(2))',
            ],
            [
              'إرسال الرسائل الخدمية ورسائل المعاملات إليك',
              'تنفيذ الاتفاقية التي تكون طرفا فيها (المادة 6(2))',
            ],
            [
              'صيانة الخدمة وتحسينها وحماية موثوقيتها وأمنها',
              'المصلحة المشروعة (المادة 6(4))، دون المساس بأي بيانات حساسة، وفي حدود توقعاتك المعقولة دائما',
            ],
            ['الامتثال للالتزامات النظامية والمحاسبية والضريبية', 'المعالجة تنفيذا لأحكام نظامية (المادة 6(2))'],
            ['إرسال الرسائل التسويقية أو الترويجية', 'موافقتك المستقلة والصريحة (المادة 5)'],
          ],
        },
        {
          t: 'note',
          c: '**موافقتك بيدك:** نطلب الموافقة لكل غرض يستلزمها على حدة، ولا نجمع أبدا بين أغراض لا صلة بينها. ولك أن تسحب موافقتك متى شئت، وسحبها بسهولة منحها.',
        },
        {
          t: 'p',
          c: 'يمكنك سحب أي موافقة في أي وقت من إعدادات حسابك، أو بمراسلتنا عبر [privacy@prinotes.com](mailto:privacy@prinotes.com)؛ ويسري السحب دون تأخير غير مبرر، ولا يمس المعالجة القائمة على أساس نظامي آخر.',
        },
      ],
    },
    {
      id: 'ai-processing',
      n: '06',
      title: 'المعالجة بالذكاء الاصطناعي',
      blocks: [
        {
          t: 'p',
          c: 'يقوم برَينوتس في ميزاته الأساسية على الذكاء الاصطناعي: تحويل الكلام إلى نص (التفريغ)، وإنشاء الملخصات والمحاضر والمهام من اجتماعاتك.',
        },
        {
          t: 'p',
          c: 'ونستعين في ذلك بمزودي ذكاء اصطناعي موثوقين من الأطراف الثالثة، هما حاليا **OpenAI** و**Google Gemini**، للتفريغ والهيكلة والتلخيص، وهما مدرجان في صفحة مزودي الخدمات.',
        },
        {
          t: 'note',
          c: '**لا تدريب على محتواك:** لا يُستخدم محتواك في تدريب نماذج الذكاء الاصطناعي، لا نماذجنا ولا نماذج مزودينا؛ فنحن نتعامل مع هؤلاء المزودين عبر باقات مدفوعة لا تدرب نماذجها على محتوى العملاء.',
        },
        {
          t: 'p',
          c: 'قد يحتفظ مزودو الذكاء الاصطناعي بالمحتوى مدة قصيرة (نحو 30 يوما*) **حصرا لكشف إساءة الاستخدام والحفاظ على أمان خدماتهم**، ثم يُحذف بعدها. فهم لا يستخدمونه في تدريب نماذجهم، ونحن بدورنا لا نحتفظ بصوتك الخام ولا نستخدمه لتدريب نماذجنا أو تحسينها.',
        },
        {
          t: 'p',
          c: '* وفقا لسياسات استخدام البيانات المنشورة لواجهات برمجة التطبيقات (API) لدى مزودينا (OpenAI و Google Gemini، في الباقات المدفوعة)، وهي باقات لا تستخدم محتوى العملاء في التدريب. وتحدد كل جهة مدد الاحتفاظ الدقيقة وقد تتغير؛ فراجع صفحة مزودي الخدمات.',
        },
        {
          t: 'p',
          c: 'وقد يأتي ناتج الذكاء الاصطناعي **ناقصا أو غير دقيق**، فراجعه قبل أن تعتمد عليه، وتجد التفاصيل في شروط الخدمة. ولا نتخذ بحقك أي قرار ينتج آثارا قانونية أو جوهرية مماثلة استنادا إلى المعالجة الآلية وحدها.',
        },
      ],
    },
    {
      id: 'recording',
      n: '07',
      title: 'التسجيل ومسؤوليتك',
      blocks: [
        {
          t: 'p',
          c: 'أنت من يُقرر ما يُسجل؛ برَينوتس يسجل على **جهازك** (من الميكروفون أو صوت النظام)، أو يعالج تسجيلات ترفعها بنفسك.',
        },
        {
          t: 'note',
          c: '**تنبيه مهم:** لا يتولى برَينوتس إشعار بقية المشاركين، ولا الحصول على موافقتهم نيابة عنك. فمسؤولية إبلاغ المشاركين، والحصول على أي موافقة يشترطها النظام قبل تسجيل الاجتماع أو تفريغه أو معالجته، **تقع على عاتقك**.',
        },
        {
          t: 'p',
          c: 'وتسجيل المحادثات مقيد نظاما في كثير من الدول، وبعضها يشترط موافقة **جميع** المشاركين. فراجع القسم 5 (الموافقة على التسجيل) من شروط الخدمة، ففيه بيان مسؤولياتك كاملة.',
        },
      ],
    },
    {
      id: 'meeting-participant',
      n: '08',
      title: 'إذا كنت مشاركا في اجتماع (ولست مستخدما لبرَينوتس)',
      blocks: [
        {
          t: 'p',
          c: 'قد يسجل أحد مستخدمي برَينوتس اجتماعا شاركت فيه، أو يرفع تسجيله. وفي هذه الحالة نعالج صوت الاجتماع ونصه المفرغ وما ينشأ عنهما من محتوى، وقد يشمل ذلك صوتك واسمك وما قلته، وذلك لتقديم الخدمة لذلك المستخدم، استنادا إلى مصلحتنا المشروعة في تشغيل الخدمة بموجب نظام حماية البيانات الشخصية. ويتحمل المستخدم الذي سجل الاجتماع مسؤولية إبلاغك والحصول على أي موافقة يشترطها النظام قبل التسجيل.',
        },
        {
          t: 'p',
          c: 'ولا نستخدم محتوى الاجتماعات للتعرف على هوية أحد بالقياسات الحيوية، ولا لتدريب نماذج الذكاء الاصطناعي. و**نحذف صوت الاجتماع من أنظمتنا فور انتهاء المعالجة**، ولا يُحفظ إلا إذا فعّل صاحب الحساب الاحتفاظ بالصوت. أما النص المفرغ وما ينشأ منه فنحتفظ به ريثما يحذفه صاحب الحساب (انظر القسم 12).',
        },
        {
          t: 'p',
          c: 'وحتى لو لم تكن مستخدما لبرَينوتس، فلك في بياناتك الشخصية الحقوق المبينة في القسم 13، ومنها الوصول والتصحيح والحذف. راسلنا عبر [privacy@prinotes.com](mailto:privacy@prinotes.com)؛ وقد نحتاج، حماية لبيانات الجميع، إلى التحقق من هويتك وإلى إشراك صاحب الحساب للوصول إلى المحتوى المعني.',
        },
      ],
    },
    {
      id: 'how-we-share',
      n: '09',
      title: 'كيف نشارك المعلومات',
      blocks: [
        {
          t: 'p',
          c: '**لا نبيع بياناتك الشخصية**، ولا نستخدم محتوى اجتماعاتك في الإعلانات.',
        },
        { t: 'p', c: 'ولا نشارك البيانات الشخصية إلا في الحالات الآتية:' },
        {
          t: 'ul',
          c: [
            '**مع مزودي الخدمات** الذين يعينوننا على تشغيل الخدمة، بموجب عقود تلزمهم بحماية بياناتك وبقصر استخدامها على تعليماتنا. وفيما يلي ملخص بهم، أما القائمة الكاملة ففي صفحة مزودي الخدمات.',
            '**امتثالا للنظام**، أو استجابة لطلب نظامي، أو إنفاذا لشروطنا، أو حماية لحقوق مستخدمينا والجمهور وسلامتهم وأمنهم.',
            '**في سياق صفقة تجارية** (كاندماج أو استحواذ)، وتبقى بياناتك عندئذ محمية بموجب هذه السياسة.',
          ],
        },
        {
          t: 'table',
          head: ['مزود الخدمات', 'الغرض', 'الموقع'],
          rows: [
            ['OpenAI', 'المعالجة بالذكاء الاصطناعي لمحتوى الاجتماعات', 'الولايات المتحدة'],
            ['Google Gemini', 'المعالجة بالذكاء الاصطناعي لمحتوى الاجتماعات', 'الولايات المتحدة / عالمي'],
            ['Oracle Cloud Infrastructure', 'الاستضافة والتخزين المشفر', 'المملكة العربية السعودية'],
            ['Cloudflare', 'توصيل المحتوى والأمن', 'الولايات المتحدة / عالمي'],
            ['Sentry', 'مراقبة الأخطاء', 'الاتحاد الأوروبي / الولايات المتحدة'],
            ['Moyasar', 'معالجة مدفوعات الباقات المدفوعة', 'المملكة العربية السعودية'],
          ],
        },
        {
          t: 'p',
          c: 'وتجد قائمتنا الكاملة والمحدثة، ومعها Google Workspace و Microsoft 365 (ولا يُستخدمان إلا إذا ربطتهما بنفسك)، في صفحة مزودي الخدمات.',
        },
      ],
    },
    {
      id: 'integrations',
      n: '10',
      title: 'التكامل مع Google وMicrosoft',
      blocks: [
        {
          t: 'p',
          c: 'إذا ربطت حسابك في Google أو Microsoft، فلا نطلب من الصلاحيات إلا ما تحتاجه الميزات التي تفعلها:',
        },
        {
          t: 'ul',
          c: [
            '**التقويم**: لقراءة المواعيد التي تُنشأ عبر برَينوتس لاجتماعاتك ومتابعاتك، وإنشائها وإدارتها.',
            '**المهام / To Do**: لإضافة المهام التي نستخلصها إلى قائمة مهامك، ومزامنة حالة إنجازها، وحذفها إذا طلبت ذلك. ويقتصر هذا على المهام التي ينشئها برَينوتس.',
            '**Drive / OneDrive**: لحفظ **أصوات اجتماعاتك ونصوصها المفرغة** في تخزينك السحابي (ويقتصر ذلك على الملفات التي ينشئها برَينوتس). و**هذا مفعّل افتراضيا** عند منحك صلاحية Drive أو OneDrive؛ ويمكنك رفض الصلاحية، أو إيقاف الحفظ التلقائي أثناء التهيئة، أو إيقافه متى شئت من الإعدادات.',
            '**جهات الاتصال**: قراءة فقط، لاقتراح المشاركين والمكلفين أثناء الكتابة.',
          ],
        },
        {
          t: 'note',
          c: '**الاستخدام المحدود:** يتقيد استخدام برَينوتس للمعلومات الواردة من واجهات برمجة Google بسياسة بيانات مستخدمي خدمات Google API، بما فيها متطلبات **الاستخدام المحدود**. كما يتقيد استخدامنا لبيانات Microsoft بشروط Microsoft. ولا نبيع أيا من بياناتك، بما فيها بياناتك من Google أو Microsoft، ولا نستخدم بياناتك من Google أو Microsoft للإعلانات أو لتدريب نماذج الذكاء الاصطناعي.',
        },
        {
          t: 'p',
          c: '**لا نخزن نسخا** من تقويمك ولا مهامك ولا ملفاتك ولا جهات اتصالك لدى Google أو Microsoft. و**جهات الاتصال تُقرأ لحظيا ولا تُخزن أبدا**: فعندما تكتب، نقرأ الأسماء وعناوين البريد من حسابك لعرض الاقتراحات فحسب، دون الاحتفاظ بأي نسخة. وكل ما نخزنه هو الحد الأدنى من المعرفات اللازمة لربط الاجتماع بعنصر أنشأته، كمعرف الموعد مثلا.',
        },
        {
          t: 'note',
          c: '**نسخ في تخزينك السحابي:** إذا ربطت Google Drive أو OneDrive، **يحفظ برَينوتس أصوات اجتماعاتك ونصوصها المفرغة** هناك تلقائيا ما لم توقف الحفظ التلقائي. وهذه الملفات ملك لك وتبقى في حسابك، ما يعني أنها **خارج سيطرتنا**: فحذف الاجتماع من برَينوتس، أو حذف حسابك، أو حذف الصوت تلقائيا من أنظمتنا، **لا** يحذف النسخ الموجودة في Drive أو OneDrive. وعليك حذفها بنفسك من حسابك لدى Google أو Microsoft.',
        },
        {
          t: 'p',
          c: 'ويمكنك إيقاف الحفظ التلقائي متى شئت من إعدادات برَينوتس، أو رفض صلاحية Drive أو OneDrive أصلا، فلا يُرسل حينها أي ملف إليها.',
        },
        {
          t: 'p',
          c: 'ويمكنك إلغاء ربط أي حساب متى شئت من إعدادات برَينوتس، أو سحب الصلاحية مباشرة من إعدادات الأمان في حسابك لدى Google أو Microsoft. وعند إلغاء الربط، نحذف رموز الوصول المخزنة لذلك الحساب.',
        },
      ],
    },
    {
      id: 'transfers',
      n: '11',
      title: 'نقل البيانات إلى خارج المملكة',
      blocks: [
        {
          t: 'p',
          c: 'تُخزن بيانات حسابك واجتماعاتك على بنية تحتية داخل **المملكة العربية السعودية**، وهي **مشفرة أثناء التخزين في كلا الموقعين**: البيانات الأساسية في الرياض، والنسخ الاحتياطية المشفرة في جدة.',
        },
        {
          t: 'p',
          c: 'ولتفريغ اجتماعاتك وتلخيصها، يُنقل محتواك **إلى خارج المملكة** لدى مزودي الذكاء الاصطناعي (الموجودين في الولايات المتحدة أو العاملين عالميا). وتجري عمليات النقل هذه وفقا لضوابط نقل البيانات الشخصية إلى خارج المملكة، وتقتصر على أدنى قدر من البيانات يلزم لتقديم الميزة، وتحكمها اتفاقيات معالجة بيانات مع كل مزود.',
        },
        {
          t: 'p',
          c: 'وأينما عُولجت بياناتك، تظل حقوقك بموجب نظام حماية البيانات الشخصية قائمة وواجبة الإنفاذ.',
        },
      ],
    },
    {
      id: 'retention',
      n: '12',
      title: 'تخزين البيانات والاحتفاظ بها وحذفها',
      blocks: [
        {
          t: 'p',
          c: 'لا نحتفظ بالبيانات الشخصية إلا ما دامت لازمة للغرض الذي جُمعت له، أو بالقدر الذي يوجبه النظام.',
        },
        {
          t: 'table',
          head: ['البيانات', 'مدة الاحتفاظ'],
          rows: [
            [
              'صوت الاجتماع (التسجيلات والملفات المرفوعة)',
              '**يُحذف فور انتهاء المعالجة. ولا يُحفظ إلا إذا فعّلت الاحتفاظ بالصوت***',
            ],
            ['النصوص المفرغة والملخصات والمهام', 'تبقى إلى أن تحذف الاجتماع أو حسابك'],
            ['بيانات الحساب', 'تبقى ما دام حسابك نشطا'],
            ['سجلات الفوترة والضرائب', 'تبقى بالمدد التي توجبها الأنظمة المحاسبية والضريبية المعمول بها'],
            ['سجلات الأمن والتدقيق', 'تبقى نحو 12 شهرا ثم تُحذف، حتى من النسخ الاحتياطية'],
            ['السجلات التشغيلية وسجلات الأخطاء', 'لا تبقى إلا بالقدر اللازم للأمن وتصحيح الأخطاء والأغراض النظامية'],
          ],
        },
        {
          t: 'p',
          c: '* لا يُحفظ إلا إذا كنت مشتركا في باقة مدفوعة وفعّلت الاحتفاظ بالصوت، وللمدة المشمولة في باقتك. انظر [صفحة الباقات](/ar/pricing) لمعرفة مدة الاحتفاظ في كل باقة.',
        },
        { t: 'h', c: 'حذف بياناتك' },
        {
          t: 'p',
          c: 'يمكنك حذف أي اجتماع متى شئت. وعند حذف حسابك، تُمحى اجتماعاتك ونصوصك المفرغة وملفاتك وتسجيلاتك **من أنظمتنا الحية**، ويُعطل الحساب ويُزال منه اسم العرض. ونحتفظ، لمنع إساءة الاستخدام وللامتثال النظامي، بسجل محدود يضم: بريدك الإلكتروني، ورابط صورة ملفك الشخصي، ومزود تسجيل الدخول الذي استخدمته (Google أو Microsoft)، والطوابع الزمنية الأساسية للحساب (تاريخ إنشائه وآخر تسجيل دخول)، مع قيد تدقيق أمني.',
        },
        {
          t: 'p',
          c: 'وعند حذف بياناتك، تُزال من أنظمتنا الحية فورا، ثم **تُطهر من نسخنا الاحتياطية المشفرة خلال 30 يوما**.',
        },
        {
          t: 'note',
          c: '**كيف تحذف بياناتك أو تصدرها:** يمكنك تنزيل نسخة من بياناتك، أو حذف حسابك نهائيا، في أي وقت من إعدادات الحساب ضمن **الخصوصية** (تنزيل بياناتي / حذف الحساب). وإن تعثر شيء أو احتجت إلى مساعدة في طلبك، فراسلنا عبر [privacy@prinotes.com](mailto:privacy@prinotes.com)',
        },
      ],
    },
    {
      id: 'your-rights',
      n: '13',
      title: 'حقوقك',
      blocks: [
        { t: 'p', c: 'يكفل لك نظام حماية البيانات الشخصية الحقوق الآتية:' },
        {
          t: 'ul',
          c: [
            '**العلم**: أن تعرف كيف تُعالج بياناتك الشخصية ولماذا.',
            '**الوصول**: أن تطلع على البيانات الشخصية التي نحتفظ بها عنك.',
            '**الحصول على نسخة**: أن تتسلم بياناتك بصيغة مقروءة وقابلة للمعالجة آليا.',
            '**التصحيح**: أن تصحح ما كان من بياناتك غير دقيق أو ناقصا أو قديما.',
            '**الحذف**: أن تحذف بياناتك متى انتفت الحاجة إليها.',
            '**سحب الموافقة**: في أي وقت، متى كانت المعالجة قائمة على الموافقة.',
          ],
        },
        {
          t: 'p',
          c: 'ويمكنك تنزيل بياناتك في جزأين: **ملف JSON** قابل للمعالجة آليا يضم ملفك الشخصي وتفاصيل اجتماعاتك ومهامك؛ أما النصوص المفرغة والملخصات والتسجيلات، فلأنها غير مضمنة في ذلك الملف، يمكنك **تصديرها من كل اجتماع على حدة بصيغة DOCX أو PDF**.',
        },
        {
          t: 'p',
          c: 'ولممارسة هذه الحقوق، استخدم أدوات التحكم في إعدادات حسابك ضمن **الخصوصية** (تنزيل بياناتك أو حذف حسابك)، أو راسلنا عبر [privacy@prinotes.com](mailto:privacy@prinotes.com) لأي طلب آخر أو إذا تعثر شيء. وسنرد خلال **30 يوما**، ويجوز أن نمدها 30 يوما أخرى إذا تطلب الطلب جهدا غير متناسب أو كان واحدا من طلبات متعددة، وعندها نشعرك بالتمديد وأسبابه.',
        },
        {
          t: 'p',
          c: 'وإذا رأيت أن حقوقك لم تحترم، فلك أن تتقدم بشكوى إلى **سدايا**، الجهة المختصة بحماية البيانات الشخصية في المملكة العربية السعودية.',
        },
      ],
    },
    {
      id: 'cookies',
      n: '14',
      title: 'ملفات الارتباط والتقنيات المماثلة',
      blocks: [
        { t: 'p', c: 'لا نستخدم إلا **ملفات الارتباط الأساسية** (Cookies) التي لا تعمل الخدمة من دونها:' },
        {
          t: 'ul',
          c: [
            'ملف **جلسة** يبقيك مسجل الدخول.',
            'ملف **أمني** قصير العمر يُستخدم أثناء عملية تسجيل الدخول.',
            'وقد يضع مزود الأمن وتوصيل المحتوى لدينا (**Cloudflare**) ملفا أمنيا لا غنى عنه إذا استدعت زيارتك فحصا أمنيا (كأن تتزامن مع هجوم على خدمتنا)، وهو لا يُستخدم للتتبع.',
          ],
        },
        {
          t: 'p',
          c: 'ويستخدم التطبيق التخزين المحلي في متصفحك لحفظ تفضيلات الواجهة، كاللغة والمظهر؛ ولا يحوي هذا التخزين أي معرفات تتبع، ولا يغادر جهازك.',
        },
        {
          t: 'p',
          c: 'ولا نستخدم **أي** ملفات ارتباط إعلانية أو تحليلية؛ فموقعنا لا يضع ملفات تتبع، ويستضيف خطوطه ذاتيا، فلا تُشارك بياناتك مع أي خدمات خطوط أو تحليلات خارجية.',
        },
        {
          t: 'p',
          c: 'ولأننا نكتفي بالملفات الأساسية، لا يلزم عرض شريط موافقة على ملفات الارتباط. ويمكنك التحكم فيها أو مسحها من متصفحك، غير أن تعطيل الأساسية منها قد يوقف عمل الخدمة.',
        },
      ],
    },
    {
      id: 'security',
      n: '15',
      title: 'أمن البيانات',
      blocks: [
        {
          t: 'p',
          c: 'نحمي بياناتك بتدابير تقنية وتنظيمية مناسبة، من أبرزها:',
        },
        {
          t: 'ul',
          c: [
            '**التشفير أثناء النقل** عبر TLS 1.2 أو أحدث، و**التشفير أثناء التخزين** عبر AES-256، مع تشفير إضافي لرموز الحسابات المرتبطة على مستوى التطبيق.',
            '**نسخ احتياطية مشفرة** قبل أن تغادر خوادمنا، وتُحفظ مفاتيح فك تشفيرها منفصلة وبمعزل عن الاتصال.',
            '**عزل بيانات كل عميل** على مستوى قاعدة البيانات، وضوابط وصول قائمة على مبدأ الحد الأدنى من الصلاحيات.',
            '**تحديد لمعدل الطلبات وجدار حماية لتطبيقات الويب**، مع تسجيل تدقيقي لكل وصول إلى البيانات.',
          ],
        },
        {
          t: 'note',
          c: '**مع ذلك:** لا توجد وسيلة نقل أو تخزين آمنة تماما؛ فنحن نبذل جهدا كبيرا لحماية بياناتك، لكننا لا نستطيع ضمان أمان مطلق.',
        },
        {
          t: 'p',
          c: '**وإن وقع خطأ ما.** إذا حدث تسرب لبيانات شخصية قد يلحق بك ضررا أو يمس حقوقك، فسنبلغ سدايا خلال 72 ساعة من علمنا به، وسنخطرك دون تأخير غير مبرر، وبلغة واضحة، بما حدث وبالمخاطر المحتملة وبتوصياتنا لحماية نفسك.',
        },
      ],
    },
    {
      id: 'who-can-access',
      n: '16',
      title: 'من يمكنه الوصول إلى بياناتك',
      blocks: [
        {
          t: 'p',
          c: 'الوصول إلى بياناتك مقصور على موظفي برييورا المخولين الذين يحتاجونه لتشغيل الخدمة أو دعمها أو تأمينها.',
        },
        {
          t: 'note',
          c: '**وصول مقيد ومدقق:** لا يصل الموظف المخول إلى محتوى اجتماعاتك إلا عبر جلسة وصول محددة المدة (ساعة واحدة كحد أقصى)، تُستخدم لأغراض الدعم والأمن فقط، وتخضع كل جلسة منها للتدقيق الكامل.',
        },
        {
          t: 'p',
          c: 'ولا نبيع بياناتك أبدا، ولا نستخدم محتوى اجتماعاتك في الإعلانات ولا في بناء ملفات تعريفية عنك.',
        },
      ],
    },
    {
      id: 'childrens-privacy',
      n: '17',
      title: 'خصوصية الأطفال',
      blocks: [
        {
          t: 'p',
          c: 'الخدمة موجهة لمن بلغ **18 عاما فأكثر**، ولا نجمع عن علم أي بيانات شخصية ممن هم دون ذلك. فإن كنت تظن أن قاصرا زودنا ببيانات شخصية، فراسلنا عبر [privacy@prinotes.com](mailto:privacy@prinotes.com) وسنحذفها.',
        },
      ],
    },
    {
      id: 'changes',
      n: '18',
      title: 'التعديلات على هذه السياسة',
      blocks: [
        {
          t: 'p',
          c: 'قد نُحدث هذه السياسة من وقت لآخر. فإذا تضمن التحديث غرض معالجة جديدا يستلزم موافقتك بموجب نظام حماية البيانات الشخصية، طلبنا موافقتك عليه على نحو مستقل قبل أن يسري عليك. وسنُحدث عندها تاريخ "آخر تحديث" أعلاه، ونقدم إشعارا إضافيا عند التعديلات الجوهرية. واستمرارك في استخدام الخدمة بعد التحديث يُعد قبولا منك للسياسة المعدلة.',
        },
      ],
    },
    {
      id: 'contact',
      n: '19',
      title: 'تواصل معنا',
      blocks: [
        {
          t: 'p',
          c: 'لأسئلة الخصوصية أو لممارسة حقوقك: [privacy@prinotes.com](mailto:privacy@prinotes.com)',
        },
        { t: 'p', c: 'للدعم العام: [support@prinotes.com](mailto:support@prinotes.com)' },
        { t: 'p', c: IDENTITY_AR },
      ],
    },
  ],
};

const TERMS_AR: LegalDoc = {
  key: 'terms',
  eyebrow: 'قانوني',
  title: 'شروط الخدمة',
  updated: 'آخر تحديث 28 يوليو 2026',
  intro:
    'تنظم شروط الخدمة هذه وصولك إلى برَينوتس واستخدامك له، وهو خدمة تقدمها شركة برييورا. وبإنشائك حسابا أو استخدامك الخدمة فإنك توافق على هذه الشروط؛ فاقرأها بعناية، ومعها سياسة الخصوصية.',
  sections: [
    {
      id: 'acceptance',
      n: '01',
      title: 'قبول الشروط',
      blocks: [
        {
          t: 'p',
          c: 'بوصولك إلى برَينوتس ("الخدمة") أو استخدامك له، فإنك توافق على الالتزام بشروط الخدمة هذه ("الشروط") وبسياسة الخصوصية. فإن لم توافق عليها، فلا تستخدم الخدمة.',
        },
        {
          t: 'p',
          c: 'وإذا كنت تستخدم الخدمة نيابة عن منشأة، فإنك تقر بأنك تملك الصلاحية لإلزامها بهذه الشروط، ويكون المقصود بـ"أنت" تلك المنشأة.',
        },
      ],
    },
    {
      id: 'eligibility',
      n: '02',
      title: 'الأهلية',
      blocks: [
        {
          t: 'p',
          c: 'يشترط لاستخدام الخدمة أن يكون عمرك **18 عاما فأكثر**، وأن تتمتع بالأهلية النظامية لإبرام العقود. ولا يجوز لك استخدام الخدمة إذا كان أي نظام معمول به يمنعك من ذلك.',
        },
      ],
    },
    {
      id: 'description',
      n: '03',
      title: 'وصف الخدمة',
      blocks: [
        {
          t: 'p',
          c: 'يعينك برَينوتس على توثيق اجتماعاتك وتنظيمها: فهو يسجل الاجتماعات أو يستقبل ما ترفعه من تسجيلاتها، ويفرغها نصوصا، ويستخدم الذكاء الاصطناعي لإنشاء الملخصات والمحاضر والقرارات والمهام منها. ويمكنه عند رغبتك المزامنة مع التقويم والمهام والملفات وجهات الاتصال في حسابك لدى Google Workspace أو Microsoft 365.',
        },
        {
          t: 'p',
          c: 'ونعمل باستمرار على تحسين الخدمة، وقد نضيف ميزات أو نغيرها أو نزيلها، وسنسعى إلى تقديم إشعار معقول قبل التغييرات الجوهرية.',
        },
      ],
    },
    {
      id: 'accounts',
      n: '04',
      title: 'الحسابات والأمان',
      blocks: [
        {
          t: 'p',
          c: 'تسجل دخولك عبر حسابك في Google أو Microsoft، وأنت مسؤول عن الحفاظ على أمان الحساب الذي تدخل به إلى برَينوتس، وعن كل نشاط يجري عبر حسابك.',
        },
        {
          t: 'p',
          c: 'وإذا كانت منشأتك تستخدم برَينوتس، فقد يتولى مشرف إدارة الأعضاء والتحكم في الإعدادات والوصول إلى بيانات مساحة العمل المرتبطة بحساب المنشأة. وأبلغنا فورا عبر [support@prinotes.com](mailto:support@prinotes.com) عن أي استخدام غير مصرح به.',
        },
      ],
    },
    {
      id: 'recording-consent',
      n: '05',
      title: 'الموافقة على التسجيل: مسؤوليتك',
      blocks: [
        {
          t: 'note',
          c: '**هذا القسم مهم، فاقرأه بعناية:** لا يوفر برَينوتس أي أدوات لإشعار المشاركين في الاجتماع أو جمع موافقاتهم؛ فالحصول على أي موافقة يشترطها النظام مسؤوليتك وحدك.',
        },
        {
          t: 'p',
          c: 'يقيد كثير من الدول تسجيل المحادثات ومعالجة أصوات المشاركين وبياناتهم الشخصية أو يشترط له شروطا، ومن ذلك المملكة العربية السعودية وسائر الدول التي قد توجد فيها أنت أو المشاركون في اجتماعاتك؛ وبعض الدول يشترط موافقة **جميع** المشاركين.',
        },
        {
          t: 'p',
          c: 'وفي المملكة العربية السعودية، قد يشكل تسجيل المحادثات الخاصة دون الموافقة المطلوبة مخالفة للأنظمة المعمول بها، ومنها أنظمة تصل عقوباتها إلى العقوبات الجزائية. وتطبق دول أخرى قواعد مماثلة أو أشد.',
        },
        {
          t: 'p',
          c: 'وأنت وحدك المسؤول عن تحديد ما إذا كان كل اجتماع تسجله يستلزم موافقة أو إشعارا، وعن الحصول على أي موافقة مطلوبة من **جميع المشاركين** وتوجيه أي إشعار لازم إليهم، قبل تسجيل الاجتماع أو تفريغه أو معالجته، ويشمل ذلك ما تنشئه ميزات الذكاء الاصطناعي في برَينوتس من ملخصات ومهام وغيرها من المخرجات. كما توافق على إبلاغ جميع المشاركين بأن الاجتماع يُسجل ويُعالج عبر برَينوتس.',
        },
        {
          t: 'p',
          c: 'وببدئك التسجيل عبر برَينوتس، فإنك تقر بأنك حصلت على جميع الموافقات والأذونات اللازمة، أو بأن التسجيل مشروع في تلك الظروف. وتوافق على تعويض برييورا عن أي مطالبة تنشأ عن إخفاقك في الحصول على تلك الموافقة أو توجيه ذلك الإشعار.',
        },
      ],
    },
    {
      id: 'ai-content',
      n: '06',
      title: 'المحتوى المُنشأ بالذكاء الاصطناعي ودقته',
      blocks: [
        {
          t: 'note',
          c: '**لا اعتماد دون مراجعة:** تُنشأ مخرجات الذكاء الاصطناعي آليا، وقد تأتي ناقصة أو غير دقيقة أو خارج سياقها أو منسوبة إلى غير قائلها، وهي ليست محضرا معتمدا ولا تفريغا حرفيا للاجتماع.',
        },
        {
          t: 'p',
          c: 'يستخدم برَينوتس الذكاء الاصطناعي لتفريغ الصوت وإنشاء الملخصات والمحاضر والقرارات والمهام ("مخرجات الذكاء الاصطناعي"). ولا نقدم أي ضمان لاكتمال أي من هذه المخرجات أو دقتها أو موثوقيتها.',
        },
        {
          t: 'p',
          c: 'وتُقدم هذه المخرجات للتيسير فحسب، وهي **لا تشكل استشارة قانونية أو مالية أو طبية أو أي استشارة مهنية أخرى**. وأنت وحدك المسؤول عن مراجعتها قبل توزيعها أو التصرف بناء عليها أو الاعتماد عليها في أي قرار أو التزام. وإلى أقصى حد يجيزه النظام، تخلي برييورا مسؤوليتها عن أي تصرف يستند إلى مخرجات لم تُراجع.',
        },
      ],
    },
    {
      id: 'your-content',
      n: '07',
      title: 'محتواك والترخيص الممنوح لبرَينوتس',
      blocks: [
        {
          t: 'p',
          c: 'تحتفظ بكامل ملكيتك للمحتوى الذي تسجله أو ترفعه أو تنشئه عبر الخدمة ("محتواك").',
        },
        {
          t: 'p',
          c: 'ولتقديم الخدمة، تمنح برييورا ترخيصا محدودا غير حصري عالمي النطاق لاستضافة محتواك وتخزينه ومعالجته وتفريغه وتلخيصه وعرضه، **حصرا لتشغيل الخدمة وتقديمها لك**. وينتهي هذا الترخيص متى حذفت محتواك أو حسابك، عدا النسخ التي تبقى مؤقتا في النسخ الاحتياطية كما تبينه سياسة الخصوصية.',
        },
        {
          t: 'p',
          c: '**نحذف صوت الاجتماع من أنظمتنا فور انتهاء المعالجة، ولا يُحفظ إلا إذا فعّلت الاحتفاظ بالصوت.*** ويمكنك إيقاف الاحتفاظ أو حذف الصوت المحفوظ متى شئت. ولا يسري الاحتفاظ بأثر رجعي على صوت حُذف من قبل.',
        },
        {
          t: 'p',
          c: '* لا يُحفظ إلا إذا كنت مشتركا في باقة مدفوعة وفعّلت الاحتفاظ بالصوت، وللمدة المشمولة في باقتك. انظر [صفحة الباقات](/ar/pricing) لمعرفة مدة الاحتفاظ في كل باقة.',
        },
        {
          t: 'p',
          c: 'وإذا ربطت Google Drive أو OneDrive، يحفظ برَينوتس أصوات اجتماعاتك ونصوصها المفرغة في تخزينك السحابي تلقائيا ما لم توقف ذلك. وهذه النسخ ملك لك وخارج سيطرتنا: فحذف المحتوى من برَينوتس لا يزيلها، وأنت المسؤول عن إدارتها في حسابك لدى Google أو Microsoft.',
        },
        {
          t: 'p',
          c: 'وتقر وتضمن أنك تملك جميع الحقوق اللازمة لرفع محتواك ومنح هذا الترخيص، وأن محتواك لا يخالف أي نظام ولا ينتهك حقوق أي طرف ثالث.',
        },
      ],
    },
    {
      id: 'acceptable-use',
      n: '08',
      title: 'الاستخدام المقبول والأنشطة المحظورة',
      blocks: [
        { t: 'p', c: 'توافق على الامتناع عما يلي:' },
        {
          t: 'ul',
          c: [
            'استخدام الخدمة لأي غرض غير مشروع أو بما يخالف أي نظام أو لائحة معمول بها.',
            'تسجيل أي اجتماع أو تفريغه أو معالجته دون الموافقة المشترطة في القسم 5.',
            'انتهاك حقوق الملكية الفكرية أو الخصوصية أو غيرها من حقوق أي شخص.',
            'رفع برمجيات خبيثة، أو محاولة الوصول غير المصرح به إلى الخدمة أو تعطيلها أو المساس بها أو بأمنها.',
            'إجراء هندسة عكسية للخدمة أو فك تجميعها أو محاولة استخراج شفرتها المصدرية، إلا فيما يجيزه النظام.',
            'إعادة بيع الخدمة أو ترخيصها للغير أو استغلالها تجاريا دون إذن كتابي مسبق منا.',
          ],
        },
      ],
    },
    {
      id: 'third-party',
      n: '09',
      title: 'خدمات الأطراف الثالثة',
      blocks: [
        {
          t: 'p',
          c: 'تتكامل الخدمة مع خدمات أطراف ثالثة مثل Google وMicrosoft و(للمدفوعات) Moyasar، ويخضع استخدامك لتلك الخدمات لشروطها وسياسات الخصوصية الخاصة بها.',
        },
        {
          t: 'p',
          c: 'وربط أي خدمة خارجية خيار يعود إليك؛ فنحن لسنا مسؤولين عن توفر خدمات الأطراف الثالثة ولا عن دقتها أو ممارساتها، وقد تؤثر تغييراتها في ميزات برَينوتس المعتمدة عليها.',
        },
      ],
    },
    {
      id: 'billing',
      n: '10',
      title: 'الاشتراكات والفوترة',
      blocks: [
        {
          t: 'p',
          c: 'يقدم برَينوتس **باقة مجانية** و**باقات مدفوعة**. وتُعرض الباقات ومزاياها وأسعارها في [صفحة الباقات](/ar/pricing).',
        },
        { t: 'h', c: 'الرسوم والضرائب' },
        {
          t: 'ul',
          c: [
            'تُحدد الرسوم **بالريال السعودي** (SAR)، وتُعرض عليك كاملة قبل تأكيد الشراء.',
            '**لا تُفرض ضريبة قيمة مضافة** حاليا؛ ولن تُطبق إلا إذا أصبحت برييورا مسجلة فيها، وسيُعرض أي تغيير عندئذ بوضوح.',
          ],
        },
        { t: 'h', c: 'الفوترة والتجديد' },
        {
          t: 'ul',
          c: [
            'تُفوتر الباقات المدفوعة مقدما بدورة **شهرية أو سنوية** حسب اختيارك عند الدفع، و**تتجدد تلقائيا** في نهاية كل دورة إلى أن تلغيها.',
            'ونخصم من وسيلة الدفع المحفوظة في تاريخ كل تجديد. وإذا أخفق الخصم، فقد نعيد المحاولة وقد نوقف المزايا المدفوعة إلى أن ينجح الدفع.',
            'ونخطرك قبل سريان أي تغيير في السعر، ويسري السعر الجديد من تجديدك التالي.',
          ],
        },
        { t: 'h', c: 'الإلغاء' },
        {
          t: 'ul',
          c: [
            'يمكنك الإلغاء متى شئت من إعدادات حسابك. ويوقف الإلغاء التجديد التالي، وتبقى المزايا المدفوعة متاحة إلى نهاية المدة التي دفعت ثمنها.',
            'وعند انتهاء الباقة المدفوعة، يُحذف الصوت المحفوظ بموجبها بعد مهلة 30 يوما، ولا يطال ذلك نصوصك المفرغة ولا ملاحظاتك.',
          ],
        },
        { t: 'h', c: 'الاسترداد' },
        {
          t: 'note',
          c: '**قبل الاشتراك:** لا تُسترد قيمة الباقة المدفوعة إلا إذا كانت **غير مستخدمة تماما**. فبمجرد معالجة اجتماع واحد أو استهلاك رصيد واحد، يسقط حق الإلغاء خلال سبعة أيام ويصبح المبلغ نهائيا.',
        },
        {
          t: 'ul',
          c: [
            'بموجب نظام التجارة الإلكترونية السعودي، يحق لك إلغاء الباقة المدفوعة واسترداد **كامل المبلغ خلال سبعة (7) أيام** من الاشتراك، **شريطة ألا تكون قد استخدمت المزايا المدفوعة إطلاقا** خلال تلك المدة.',
            '**واستخدام أي جزء من باقتك يسقط هذا الحق.** فمعالجة اجتماع واحد، أو استهلاك رصيد واحد، أو الحصول على أي منفعة من المزايا المدفوعة، يعني أن الباقة استُخدمت، ومن ثم **لا يُسترد الاشتراك**. ويسري ذلك مهما قل الاستخدام: فلا استرداد جزئيا ولا تناسبيا عن الأرصدة أو المدد غير المستخدمة.',
            '**ولا تُسترد مدفوعات التجديد**، إلا إذا كانت الخدمة غير متاحة أو لم تعمل على النحو الموصوف.',
            'ومتى استحق الاسترداد، نعيد المبلغ **بالوسيلة نفسها التي دفعت بها، خلال 15 يوما**.',
            'وخارج هذه الحالات، نراجع كل طلب ونسترد وفق تقديرنا متى كان الخلل من جانبنا. راسلنا على [support@prinotes.com](mailto:support@prinotes.com)',
            'ولا يحد أي مما ورد في هذه الشروط من أي حق يكفله لك نظام التجارة الإلكترونية السعودي أو لائحته التنفيذية.',
          ],
        },
        { t: 'h', c: 'المدفوعات' },
        {
          t: 'ul',
          c: [
            'تتولى **Moyasar**، وهي جهة دفع مرخصة في المملكة العربية السعودية، معالجة المدفوعات. و**لا يخزن برَينوتس بيانات بطاقتك أبدا**؛ إذ تحول Moyasar بيانات الدفع إلى رموز آمنة، ولا نحتفظ إلا بنوع البطاقة وأرقامها الأربعة الأخيرة.',
            'وتتوفر الفواتير وسجل المدفوعات في إعدادات حسابك.',
          ],
        },
      ],
    },
    {
      id: 'ip',
      n: '11',
      title: 'الملكية الفكرية',
      blocks: [
        {
          t: 'p',
          c: 'الخدمة، بما فيها برمجياتها وتصميمها وعلامتها ومحتواها (عدا محتواك)، مملوكة لشركة برييورا ومحمية بأنظمة الملكية الفكرية. ونمنحك حقا محدودا غير حصري وغير قابل للنقل لاستخدام الخدمة وفقا لهذه الشروط، ولا يمنحك ذلك أي حقوق أخرى.',
        },
      ],
    },
    {
      id: 'feedback',
      n: '12',
      title: 'الملاحظات والاقتراحات',
      blocks: [
        {
          t: 'p',
          c: 'إذا أرسلت إلينا اقتراحات أو ملاحظات عن الخدمة، فإنك تمنح برييورا ترخيصا دائما غير قابل للإلغاء وبلا مقابل لاستخدامها دون قيد ولا التزام تجاهك.',
        },
      ],
    },
    {
      id: 'disclaimers',
      n: '13',
      title: 'إخلاء الضمانات',
      blocks: [
        {
          t: 'p',
          c: 'تُقدم الخدمة **"كما هي" و"بحسب توفرها"**. وإلى أقصى حد يجيزه النظام، نخلي مسؤوليتنا عن جميع الضمانات، الصريحة منها والضمنية، بما فيها ضمانات القابلية للتسويق والملاءمة لغرض معين وعدم التعدي على حقوق الغير. ولا نضمن أن تعمل الخدمة دون انقطاع أو خطأ، ولا أن تكون مخرجات الذكاء الاصطناعي دقيقة.',
        },
      ],
    },
    {
      id: 'liability',
      n: '14',
      title: 'حدود المسؤولية',
      blocks: [
        {
          t: 'p',
          c: 'إلى أقصى حد يجيزه النظام، لا تتحمل برييورا أي مسؤولية عن أي أضرار غير مباشرة أو عرضية أو خاصة أو تبعية أو عقابية، ولا عن أي فقد للبيانات أو الأرباح أو الإيرادات، ينشأ عن استخدامك الخدمة.',
        },
        {
          t: 'p',
          c: 'ولا يتجاوز إجمالي مسؤوليتنا الناشئة عن الخدمة أو المتصلة بها الأعلى من: (أ) مجموع ما دفعته لنا مقابل الخدمة خلال الاثني عشر شهرا السابقة للواقعة التي نشأت عنها المطالبة، أو (ب) 500 ريال سعودي.',
        },
        {
          t: 'p',
          c: 'وليس في هذه الشروط ما يستبعد أو يحد المسؤولية عن الاحتيال أو سوء التصرف المتعمد أو الإهمال الجسيم، أو أي مسؤولية لا يجوز استبعادها أو الحد منها بموجب أنظمة المملكة العربية السعودية.',
        },
      ],
    },
    {
      id: 'indemnification',
      n: '15',
      title: 'التعويض',
      blocks: [
        {
          t: 'p',
          c: 'توافق على تعويض شركة برييورا وموظفيها ودرء الضرر عنهم من أي مطالبة أو خسارة أو نفقة (بما فيها أتعاب المحاماة المعقولة) تنشأ عن استخدامك الخدمة، أو عن محتواك، أو عن إخفاقك في الحصول على أي موافقة تسجيل مطلوبة، أو عن إخلالك بهذه الشروط.',
        },
      ],
    },
    {
      id: 'termination',
      n: '16',
      title: 'التعليق والإنهاء',
      blocks: [
        {
          t: 'p',
          c: 'لك أن تتوقف عن استخدام الخدمة وتحذف حسابك متى شئت. ولنا أن نعلق وصولك إلى الخدمة أو ننهيه إذا أخللت بهذه الشروط، أو متى أوجب النظام ذلك، أو حماية للخدمة ومستخدميها.',
        },
        {
          t: 'p',
          c: 'وعند الإنهاء ينتهي حقك في استخدام الخدمة. ويجوز لك خلال 30 يوما بعده (ما لم يوجب النظام الحذف قبل ذلك) طلب تصدير محتواك كما هو مبين في سياسة الخصوصية. وتظل الأحكام التي تقتضي طبيعتها البقاء بعد الإنهاء نافذة، ومنها الملكية الفكرية وإخلاء الضمانات وحدود المسؤولية والتعويض.',
        },
      ],
    },
    {
      id: 'governing-law',
      n: '17',
      title: 'النظام الواجب التطبيق وتسوية المنازعات',
      blocks: [
        { t: 'p', c: 'تخضع هذه الشروط لأنظمة **المملكة العربية السعودية**.' },
        {
          t: 'p',
          c: 'فإذا نشأ نزاع، سعى الطرفان أولا بحسن نية إلى تسويته بالتفاوض؛ فإن تعذر ذلك، كان النزاع خاضعا للاختصاص القضائي الحصري **للمحاكم المختصة في جدة** بالمملكة العربية السعودية.',
        },
      ],
    },
    {
      id: 'changes',
      n: '18',
      title: 'التعديلات على هذه الشروط',
      blocks: [
        {
          t: 'p',
          c: 'قد نُحدث هذه الشروط من وقت لآخر. وسنُحدث عندها تاريخ "آخر تحديث" أعلاه، ونقدم إشعارا إضافيا عند التعديلات الجوهرية. واستمرارك في استخدام الخدمة بعد التحديث يُعد قبولا منك للشروط المعدلة.',
        },
      ],
    },
    {
      id: 'contact',
      n: '19',
      title: 'تواصل معنا',
      blocks: [
        {
          t: 'p',
          c: 'لأي سؤال عن هذه الشروط، راسلنا عبر [support@prinotes.com](mailto:support@prinotes.com)',
        },
        { t: 'p', c: IDENTITY_AR },
      ],
    },
  ],
};

const SUBPROCESSORS_AR: LegalDoc = {
  key: 'subprocessors',
  eyebrow: 'قانوني',
  title: 'مزودو الخدمات',
  updated: 'آخر تحديث 28 يوليو 2026',
  intro:
    'لتقديم برَينوتس، تستعين شركة برييورا بعدد محدود من الأطراف الثالثة الموثوقة التي تعالج البيانات الشخصية نيابة عنا. ونلزم كلا منها تعاقديا بحماية بياناتك وبقصر معالجتها على تعليماتنا. وتعرض هذه الصفحة مزودي الخدمات الحاليين لدينا، ونُحدثها كلما طرأ عليها تغيير.',
  sections: [
    {
      id: 'infrastructure',
      n: '01',
      title: 'البنية التحتية والاستضافة',
      blocks: [
        {
          t: 'table',
          head: ['مزود الخدمات', 'الغرض', 'البيانات المعالجة', 'الموقع'],
          rows: [
            [
              'Oracle Cloud Infrastructure (OCI)',
              'الاستضافة السحابية وقاعدة البيانات والتخزين المشفر للملفات',
              'جميع بيانات الحسابات والاجتماعات المخزنة',
              'المملكة العربية السعودية (الرياض؛ والنسخ الاحتياطية في جدة)',
            ],
            [
              'Cloudflare',
              'توصيل المحتوى وتشفير الاتصال (TLS) والأمن (جدار الحماية والحماية من البرمجيات الآلية)',
              'عنوان IP والبيانات الوصفية للاتصال',
              'الولايات المتحدة / عالمي',
            ],
            [
              'Sentry',
              'مراقبة الأخطاء والأداء',
              'بيانات الأخطاء التشخيصية (مهيأة لتقليل البيانات الشخصية وترشيحها قبل إرسالها)',
              'الاتحاد الأوروبي / الولايات المتحدة',
            ],
          ],
        },
      ],
    },
    {
      id: 'ai-providers',
      n: '02',
      title: 'مزودو الذكاء الاصطناعي',
      blocks: [
        {
          t: 'p',
          c: 'نستعين بالمزودين أدناه مجتمعين للتفريغ والهيكلة والتلخيص، وحماية لمنتجنا لا نفصح عن المزود القائم على كل وظيفة بعينها.',
        },
        {
          t: 'table',
          head: ['مزود الخدمات', 'الغرض', 'البيانات المعالجة', 'الموقع'],
          rows: [
            [
              'OpenAI',
              'المعالجة بالذكاء الاصطناعي لمحتوى الاجتماعات',
              'صوت الاجتماع أو نصه المفرغ أو كلاهما',
              'الولايات المتحدة',
            ],
            [
              'Google Gemini',
              'المعالجة بالذكاء الاصطناعي لمحتوى الاجتماعات',
              'صوت الاجتماع أو نصه المفرغ أو كلاهما',
              'الولايات المتحدة / عالمي',
            ],
          ],
        },
        {
          t: 'note',
          c: '**كيف يُعامل محتواك:** قد يحتفظ مزودو الذكاء الاصطناعي بالمحتوى مدة قصيرة (نحو 30 يوما*) **حصرا لكشف إساءة الاستخدام والحفاظ على أمان خدماتهم**، ثم يُحذف بعدها. فهم لا يستخدمونه في تدريب نماذجهم، ونحن بدورنا لا نحتفظ بصوتك الخام ولا نستخدمه لتدريب نماذجنا أو تحسينها. و**في أنظمتنا نحن، يُحذف صوت الاجتماع فور انتهاء المعالجة، ولا يُحفظ إلا إذا فعّلت الاحتفاظ بالصوت.**†',
        },
        {
          t: 'p',
          c: '* وفقا لسياسات استخدام البيانات المنشورة لواجهات برمجة التطبيقات (API) لدى مزودينا (OpenAI و Google Gemini، في الباقات المدفوعة)، وهي باقات لا تستخدم محتوى العملاء في التدريب. وتحدد كل جهة مدد الاحتفاظ الدقيقة وقد تتغير.',
        },
        {
          t: 'p',
          c: '† لا يُحفظ إلا إذا كنت مشتركا في باقة مدفوعة وفعّلت الاحتفاظ بالصوت، وللمدة المشمولة في باقتك. انظر [صفحة الباقات](/ar/pricing) لمعرفة مدة الاحتفاظ في كل باقة.',
        },
      ],
    },
    {
      id: 'connected-accounts',
      n: '03',
      title: 'الحسابات المرتبطة (عند ربطها فقط)',
      blocks: [
        {
          t: 'p',
          c: 'لا تُستخدم الخدمتان الآتيتان إلا إذا اخترت ربطهما، وفي حدود الميزات التي تفعلها فقط. ويخضع استخدامك لهما أيضا لشروط مزوديهما.',
        },
        {
          t: 'table',
          head: ['مزود الخدمات', 'الغرض', 'البيانات المعالجة', 'الموقع'],
          rows: [
            [
              'Google Workspace',
              'مزامنة التقويم والمهام وDrive وجهات الاتصال',
              'مواعيد التقويم، والمهام، والملفات التي تنشئها، وجهات الاتصال (تُقرأ لحظيا)',
              'الولايات المتحدة / عالمي',
            ],
            [
              'Microsoft 365 (Graph)',
              'مزامنة التقويم وTo Do وOneDrive وجهات الاتصال',
              'مواعيد التقويم، والمهام، والملفات التي تنشئها، وجهات الاتصال (تُقرأ لحظيا)',
              'الولايات المتحدة / عالمي',
            ],
          ],
        },
      ],
    },
    {
      id: 'payments',
      n: '04',
      title: 'المدفوعات',
      blocks: [
        {
          t: 'table',
          head: ['مزود الخدمات', 'الغرض', 'البيانات المعالجة', 'الموقع'],
          rows: [
            [
              'Moyasar',
              'معالجة مدفوعات الاشتراكات المدفوعة',
              'الاسم والبريد الإلكتروني وتفاصيل الدفع (تعالج Moyasar بيانات البطاقة وتحولها إلى رموز آمنة، ولا يخزن برَينوتس سوى نوع البطاقة وأرقامها الأربعة الأخيرة)',
              'المملكة العربية السعودية',
            ],
          ],
        },
      ],
    },
    {
      id: 'changes',
      n: '05',
      title: 'التعديلات على هذه القائمة',
      blocks: [
        {
          t: 'p',
          c: 'نُحدث هذه الصفحة كلما أضفنا مزود خدمات أو أزلناه، ويعكس تاريخ "آخر تحديث" أعلاه آخر تغيير. ولأي سؤال عن مزودي الخدمات لدينا، راسلنا عبر [privacy@prinotes.com](mailto:privacy@prinotes.com)',
        },
      ],
    },
  ],
};

export const LEGAL_DOCS_AR: Partial<Record<LegalDoc['key'], LegalDoc>> = {
  privacy: PRIVACY_AR,
  terms: TERMS_AR,
  subprocessors: SUBPROCESSORS_AR,
};
